import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { AREAS } from "@/data/areas";
import { useLeadForm } from "@/components/lead/LeadForm";
import { SEO, breadcrumbLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { CTASection } from "@/components/site/CTASection";

// Genuine area pages will be added here as the business confirms its service map.
// Birmingham is the confirmed primary area; surrounding locations are served
// and confirmed per enquiry rather than listed speculatively.
export default function AreasWeCover() {
  const { openForm } = useLeadForm();
  return (
    <>
      <SEO
        title="Areas We Cover | Blinds & Shutters Fitted Across Birmingham"
        description="My Blinds Company provides home consultations, professional measuring and fitting for made-to-measure blinds and shutters across Birmingham and surrounding areas."
        path="/areas-we-cover"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Areas We Cover", path: "/areas-we-cover" },
          ]),
        ]}
      />
      <section className="bg-warm-50 grain py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="justify-center">Areas We Cover</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-slate-900 tracking-tight leading-tight">
              Blinds & shutters, fitted across Birmingham
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
              From our base in Small Heath, we provide free home consultations,
              professional measuring and expert fitting throughout Birmingham
              and the surrounding area.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-[2rem] border border-warm-200 bg-white p-8 sm:p-12 shadow-sm">
              <div className="flex items-start gap-5">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-navy-900">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-serif text-3xl text-slate-900 tracking-tight">
                    Birmingham — our home ground
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">
                    Birmingham is where we're based and where the majority of
                    our work takes place. From city-centre apartments to the
                    terraces, semis and new developments across the city, we
                    measure and fit made-to-measure blinds and shutters in
                    homes and businesses of every kind.
                  </p>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">
                    We also serve the surrounding towns across the West
                    Midlands. Follow the links below for genuinely local
                    information for each area — or send us your postcode with
                    your enquiry and we'll confirm coverage and book your free
                    visit.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    <Link
                      to="/blinds-birmingham"
                      data-testid="areas-link-birmingham"
                      className="inline-flex items-center gap-1.5 rounded-full bg-navy-900 px-4 py-2 text-xs font-semibold text-white hover:bg-navy-800 transition-colors"
                    >
                      <MapPin className="h-3 w-3" /> Birmingham
                    </Link>
                    {AREAS.map((a) => (
                      <Link
                        key={a.slug}
                        to={`/areas/${a.slug}`}
                        data-testid={`areas-link-${a.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-warm-300 px-4 py-2 text-xs font-semibold text-navy-900 hover:border-navy-800/40 transition-colors"
                      >
                        <MapPin className="h-3 w-3" /> {a.name}
                      </Link>
                    ))}
                  </div>
                  <button
                    type="button"
                    data-testid="areas-check-postcode-btn"
                    onClick={() => openForm({ source: "areas_page" })}
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy-900 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-navy-900/10 transition-all duration-300 hover:bg-navy-800 hover:-translate-y-0.5"
                  >
                    Check Your Postcode — Book a Free Visit <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection source="areas_final_cta" />
    </>
  );
}
